import { Flex } from '../UI/Flex/Flex';
import * as style from './ChatMessage.module.less';

import { ChatMessageData } from '@assets/model/RagAPI';
import { useStore } from '@assets/model/Store';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { LogoBird } from '../Header/Logo';
import { GetIcon, Icon } from '../Icons/Icons';
import {
    cleanFilenameForDisplay,
    convertMarkdown,
    fixTableSeparators,
    extractCitationsFromTableCells
} from '@assets/utils/Utils';
import { Tooltip } from 'react-tooltip';

interface ChatMessageProps {
    msg: ChatMessageData;
    response?: boolean;
}

const findValueFromSource = (text: string, key: string): string => {
    const values = text.split('|||');
    for (const val of values) {
        if (val.toLowerCase().startsWith(key.toLowerCase() + ':')) {
            return val.slice(key.length + 1).trim();
        }
    }
    return 'Unknown';
};

const makeTooltip = (text: string, tooltipId: string) => {
    if (tooltipId == 'msg_msg_1_flowise_source_7') {
        debugger;
    }

    const sourceText = convertMarkdown(findValueFromSource(text, 'SOURCE'));
    const fileName = findValueFromSource(text, 'NAME');
    const pagesText = findValueFromSource(text, 'PAGES');
    const urlText = findValueFromSource(text, 'URL');
    const hasUrl = urlText && urlText !== 'Unknown' && urlText.startsWith('http');

    const sourceNameEl = hasUrl ? (
        <a
            href={urlText}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#90caf9', textDecoration: 'underline' }}
        >
            {fileName}
        </a>
    ) : (
        <span>{fileName}</span>
    );

    const hasPages = pagesText && pagesText !== 'Unknown' && pagesText.trim() !== '';

    return (
        <Tooltip
            clickable={true}
            key={tooltipId}
            id={tooltipId}
            delayShow={250}
            delayHide={200}
            style={{
                maxWidth: '560px',
                zIndex: 9999,
                position: 'absolute',
                backgroundColor: '#1a2f4a',
                borderRadius: '10px',
                padding: '0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
            }}
        >
            <Flex direction="column" style={{ gap: '0' }}>
                <div
                    style={{
                        overflow: 'auto',
                        maxHeight: '280px',
                        maxWidth: '560px',
                        scrollbarColor: '#4a6fa5 transparent',
                        scrollbarWidth: 'thin',
                        fontSize: '14px',
                        lineHeight: '1.55',
                        color: '#cdd9e8',
                        padding: '16px 16px 12px 16px',
                        fontStyle: 'italic'
                    }}
                    dangerouslySetInnerHTML={{ __html: sourceText }}
                />
                <div style={{
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7a9bc0', fontWeight: 600 }}>Source</span>
                        <span style={{ fontSize: '13px', color: '#e0eaf5', fontWeight: 500 }}>{sourceNameEl}</span>
                    </div>
                    {hasPages && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right', flexShrink: 0 }}>
                            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7a9bc0', fontWeight: 600 }}>Pages</span>
                            <span style={{ fontSize: '13px', color: '#e0eaf5' }}>{pagesText}</span>
                        </div>
                    )}
                </div>
            </Flex>
        </Tooltip>
    );
};

interface ITooltipData {
    id: string;
    content: string;
}

export const ChatMessage = observer((p: ChatMessageProps) => {
    const [tooltips, setTooltips] = useState<ITooltipData[]>([]);

    const msg: ChatMessageData = p.msg;
    const firmURL = useStore().firmInfo.logoURL;
    const clssses = classNames(style.chatmessage, 'fade-in');
    const companyId = useStore().selectedReportId;

    const buildTooltipId = (citationDisplayId: string) => {
        return `tooltip_${companyId}_${citationDisplayId}`;
    };

    const getAttachmentsDisplay = () => {
        const attachments_all = p.msg.attachedFiles;
        if (attachments_all == null || attachments_all.length == 0) {
            return null;
        }
        const getAttachmentIcon = (file: string) => {
            const toLower = file.toLowerCase();
            if (toLower.includes('.pdf')) {
                return GetIcon('file-pdf');
            } else {
                return GetIcon('file-md');
            }
        };
        const attachments_el = [];
        for (const attachment of attachments_all) {
            const el = (
                <div
                    key={attachment.name}
                    style={{
                        backgroundColor: 'hsl(0 0% 96% / 1)',
                        padding: '10px',
                        borderRadius: '8px',
                        textAlign: 'right',
                        marginRight: '10px',
                        alignSelf: 'end'
                    }}
                >
                    {cleanFilenameForDisplay(attachment.name)}
                    <img
                        src={getAttachmentIcon(attachment.name)}
                        style={{
                            marginLeft: '10px',
                            width: '32px',
                            height: '32px'
                        }}
                    />
                </div>
            );
            attachments_el.push(el);
        }
        return (
            <Flex style={{ width: '100%' }} justify="end">
                {attachments_el}
            </Flex>
        );
    };
    const convertMarkdown = (markdown: string): string => {
        if (!markdown) {
            return 'ERROR: No message content';
        }

        const cls = style.citation_class;

        const flowiseSourcesTooltip: ITooltipData[] = [];
        let tooltipId = 0;

        // Extract [[[...]]] citations BEFORE marked.parse so URLs inside are not mangled
        const placeholderMap: Record<string, { id: string; name: string }> = {};
        const withFlowisePlaceholders = markdown.replace(
            /\[\[\[([\s\S]*?)\]\]\]/g,
            (match, p1) => {
                const id = `msg_${p.msg.id}_flowise_source_${tooltipId}`;
                flowiseSourcesTooltip.push({ id, content: p1 });

                let tooltipName = findValueFromSource(p1, 'TITLE');
                if (tooltipName.length > 40) {
                    tooltipName = tooltipName.substring(0, 37) + '...';
                }
                const placeholder = `FLOWISECITE${tooltipId}`;
                placeholderMap[placeholder] = { id, name: tooltipName };
                tooltipId += 1;
                return placeholder;
            }
        );

        const rawHtmlWithDefault = marked.parse(
            fixTableSeparators(extractCitationsFromTableCells(withFlowisePlaceholders)),
            { async: false }
        );

        var matchCounter = 0;
        const ragTooltips = (rawHtmlWithDefault as string).replace(
            /(\[\d+\])/g,
            (match, p1) => {
                const p2 = p1.replace(/[\[\]]/g, '');
                matchCounter++;
                const tooltipId = buildTooltipId(p2);
                return `<a class='${cls}' data-tooltip-id="${tooltipId}">${matchCounter}</a>`;
            }
        );

        const finalOutput = ragTooltips.replace(
            /FLOWISECITE(\d+)/g,
            (match, idx) => {
                const entry = placeholderMap[`FLOWISECITE${idx}`];
                if (!entry) return match;
                return `<a class='${cls}' data-tooltip-id="${entry.id}">${entry.name}</a>`;
            }
        );

        if (tooltips.length != flowiseSourcesTooltip.length) {
            setTooltips(flowiseSourcesTooltip);
        }

        return DOMPurify.sanitize(finalOutput as string);
    };

    if (msg.isMe) {
        const dangerousHtml = convertMarkdown(msg.message);
        return (
            <>
                <Flex className={clssses} direction="column" alignItems="end">
                    {getAttachmentsDisplay()}
                    <div />
                    <div
                        className={style.userBubble}
                        dangerouslySetInnerHTML={{ __html: dangerousHtml }}
                    ></div>
                </Flex>
                {/* {files_references} */}
            </>
        );
    }

    const formatterHTML = convertMarkdown(msg.message);

    const agentClassAdd = msg.role === 'agentMessage' ? style.agentMessage : '';
    return (
        <>
            {tooltips.map(t => makeTooltip(t.content, t.id))}
            <Flex className={clssses} direction="row">
                <div
                    style={{
                        marginRight: '16px',
                        flexShrink: 0,
                        alignSelf: 'flex-start'
                    }}
                >
                    <LogoBird />
                </div>
                <div
                    className={`${style.aiContent} ${agentClassAdd}`}
                    style={{ marginRight: '48px' }}
                >
                    <div dangerouslySetInnerHTML={{ __html: formatterHTML }} />
                </div>
            </Flex>
            {/* {files_references} */}
        </>
    );
});
